/**
 * Application-wide antd theme contract.
 *
 * Extracted from App.tsx so the shared component contract suites can assert
 * the exact theme the running app installs (intent-20 goal-158 m1) without
 * importing the whole route graph.
 */

import { theme } from 'antd';
import type { ThemeConfig } from 'antd';

/** Minimum touch-target size (CSS px) required on mobile list/read surfaces. */
export const MOBILE_TOUCH_TARGET_PX = 44;

/**
 * Build the antd theme for the current posture.
 *
 * Touch-target contract (intent-20 goal-157 m2 + goal-158 m1):
 * - `controlHeight` 44 on mobile (320–767 CSS px) raises every default-size
 *   control (toolbar buttons, search inputs, selects, pagination items).
 * - antd derives all small-size controls from `controlHeightSM`
 *   (`controlHeight * 0.75`), so under the m2 token alone that family stayed
 *   at 33px — the mobile page-size selector, the responsive table pagination
 *   at the xs boundary (antd downsizes `responsive` pagination to small below
 *   576px), and icon-only `size="small"` action buttons (ActionsColumn) all
 *   remained below the 44px floor. The mobile branch therefore pins
 *   `controlHeightSM` to the same floor. Desktop/tablet keeps the derived
 *   27px compact posture (`controlHeight` 36).
 */
export function buildAppTheme(isMobile: boolean): ThemeConfig {
  return {
    algorithm: theme.defaultAlgorithm,
    token: {
      // Primary color - darkened from #1677ff to #0958d9 for WCAG 2 AA contrast (4.56:1)
      colorPrimary: '#0958d9',
      controlHeight: isMobile ? MOBILE_TOUCH_TARGET_PX : 36,
      ...(isMobile ? { controlHeightSM: MOBILE_TOUCH_TARGET_PX } : {}),
      borderRadius: 6,
      // Secondary text - darkened from #8c8c8c to #595959 for WCAG 2 AA contrast (7:1)
      colorTextSecondary: '#595959',
      // Placeholder text - darkened for better visibility and contrast
      colorTextPlaceholder: '#8c8c8c',
      // Disabled state - improved visibility
      colorTextDisabled: '#bfbfbf',
      colorBgContainerDisabled: '#f5f5f5',
    },
    components: {
      Button: {
        // Primary button text color with sufficient contrast
        primaryColor: '#ffffff',
      },
      Input: {
        // Placeholder color with improved contrast
        colorTextPlaceholder: '#8c8c8c',
      },
      Select: {
        // Select placeholder color with improved contrast
        colorTextPlaceholder: '#8c8c8c',
      },
      ...(isMobile
        ? {
            Switch: {
              // Goal-159 m1 form-surface contract: the Switch track sizes from
              // `fontSize * lineHeight` (22px), not controlHeight, so the one
              // form control below the mobile touch floor is pinned here. The
              // handle follows the track; index.css centers it.
              trackHeight: MOBILE_TOUCH_TARGET_PX,
              handleSize: 28,
            },
          }
        : {}),
      Typography: {
        // Secondary text color with WCAG AA compliant contrast
        // Ant Design 5 uses colorTextDescription for Typography type="secondary"
        colorTextDescription: '#595959',
      },
    },
  };
}
